import { apiService } from './api';
import { 
  SimplifiedCreateAlertRequest, 
  SimplifiedCreateAlertResponse,
  ApiAlertResponse
} from '../types/alerts';

// Types for Alert API
export interface AlertCondition {
  alertConditionId?: number;
  pollutantParameterId: number;
  pollutantParameterName: string;
  parameterName?: string;
  parameterCode?: string;
  operator: string;
  thresholdValue: number;
  parameterUnitId: number;
  parameterUnitName: string;
  unitName?: string;
  unitSymbol?: string;
}

export interface AlertRecipient {
  alertRecipientId?: number;
  deviceId?: number;
  email: string;
  name: string;
  isActive?: boolean;
}

export interface AlertData {
  alertId: number;
  alertName: string;
  description: string;
  deviceId: number;
  deviceName: string;
  createdByUserId: number;
  createdByUserName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
  lastTriggeredAt: string | null;
  triggerCount: number;
  alertConditions: AlertCondition[];
  alertRecipients: AlertRecipient[];
}

export interface CreateAlertRequest {
  alertName: string;
  description: string;
  deviceId: number;
  conditions: Omit<AlertCondition, 'alertConditionId' | 'parameterName' | 'parameterCode' | 'unitName' | 'unitSymbol'>[];
  recipients: Omit<AlertRecipient, 'alertRecipientId' | 'isActive'>[];
}

export interface UpdateAlertRequest {
  alertName: string;
  description: string;
  deviceId: number;
  conditions: (Omit<AlertCondition, 'parameterName' | 'parameterCode' | 'unitName' | 'unitSymbol'> & { alertConditionId?: number })[];
  recipients: (Omit<AlertRecipient, 'isActive'> & { alertRecipientId?: number })[];
}

export interface AlertListResponse {
  alerts: ApiAlertResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AlertFilters {
  searchTerm?: string;
  deviceId?: number;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface AlertHistoryItem {
  alertHistoryId: number;
  alertId: number;
  alertName: string;
  triggeredAt: string;
  triggeredConditions: string;
  triggeredValue: number;
  pollutantParameterId: number;
  parameterName: string;
  status: string;
  errorMessage: string | null;
  recipientsCount: number;
  successfulDeliveries: number;
  failedDeliveries: number;
}

// Alert API Service
export const alertApi = {
  // Simplified alert creation - new simplified API
  createSimplifiedAlert: async (alertData: SimplifiedCreateAlertRequest): Promise<SimplifiedCreateAlertResponse> => {
    console.log('alertApi.createSimplifiedAlert called with:', alertData);
    
    // Convert simplified format to API format
    const apiAlertData = {
      alertName: alertData.alertName,
      description: `Alert for device ${alertData.deviceId}`,
      deviceId: alertData.deviceId,
      alertConditions: alertData.conditions.map(condition => {
        // Ensure we have a valid parameter ID
        if (!condition.pollutantParameterId || condition.pollutantParameterId <= 0) {
          console.error('Invalid pollutantParameterId:', condition.pollutantParameterId, 'for condition:', condition);
          throw new Error(`Invalid parameter ID for condition: ${condition.parameter}`);
        }
        
        return {
          pollutantParameterId: condition.pollutantParameterId,
          operator: condition.operator,
          thresholdValue: condition.value,
          parameterUnitId: condition.parameterUnitId // Map unitId to parameterUnitId
        };
      }),
      alertRecipients: alertData.recipients.map(recipient => ({
        email: recipient.email,
        name: recipient.name
      }))
    };

    console.log('alertApi.createSimplifiedAlert - API data being sent:', apiAlertData);

    const response = await apiService.request<SimplifiedCreateAlertResponse>('/alert', {
      method: 'POST',
      body: JSON.stringify(apiAlertData)
    });
    
    console.log('alertApi.createSimplifiedAlert - API response:', response);
    
    if (!response.success || !response.data) {
      console.error('alertApi.createSimplifiedAlert - API call failed:', response.error);
      throw new Error(response.error || 'Failed to create alert');
    }
    
    console.log('alertApi.createSimplifiedAlert - Success, returning:', response.data);
    return response.data;
  },

  // Get all alerts with filtering and pagination
  getAlerts: async (filters: AlertFilters = {}): Promise<AlertListResponse> => {
    const params = new URLSearchParams();
    
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.deviceId) params.append('deviceId', filters.deviceId.toString());
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await apiService.request<AlertListResponse>(`/alert?${params.toString()}`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch alerts');
    }
    return response.data;
  },

  // Get alert by ID
  getAlertById: async (alertId: number): Promise<AlertData> => {
    const response = await apiService.request<AlertData>(`/alert/${alertId}`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch alert');
    }
    return response.data;
  },

  // Create new alert
  createAlert: async (alertData: CreateAlertRequest): Promise<AlertData> => {
    const response = await apiService.request<AlertData>('/alert', {
      method: 'POST',
      body: JSON.stringify(alertData)
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create alert');
    }
    return response.data;
  },

  // Update alert
  updateAlert: async (alertId: number, alertData: UpdateAlertRequest): Promise<AlertData> => {
    const response = await apiService.request<AlertData>(`/alert/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(alertData)
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update alert');
    }
    return response.data;
  },

  // Simplified alert update - new simplified API
  updateSimplifiedAlert: async (alertId: number, alertData: SimplifiedCreateAlertRequest): Promise<SimplifiedCreateAlertResponse> => {
    console.log('alertApi.updateSimplifiedAlert called with:', { alertId, alertData });
    
    // Convert simplified format to API format
    const apiAlertData = {
      alertName: alertData.alertName,
      description: `Alert for device ${alertData.deviceId}`,
      deviceId: alertData.deviceId,
      alertConditions: alertData.conditions.map(condition => {
        // Ensure we have a valid parameter ID
        if (!condition.pollutantParameterId || condition.pollutantParameterId <= 0) {
          console.error('Invalid pollutantParameterId:', condition.pollutantParameterId, 'for condition:', condition);
          throw new Error(`Invalid parameter ID for condition: ${condition.parameter}`);
        }
        
        return {
          pollutantParameterId: condition.pollutantParameterId,
          operator: condition.operator,
          thresholdValue: condition.value,
          parameterUnitId: condition.parameterUnitId
        };
      }),
      alertRecipients: alertData.recipients.map(recipient => ({
        email: recipient.email,
        name: recipient.name
      }))
    };

    console.log('alertApi.updateSimplifiedAlert - API data being sent:', apiAlertData);

    const response = await apiService.request<SimplifiedCreateAlertResponse>(`/alert/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(apiAlertData)
    });
    
    console.log('alertApi.updateSimplifiedAlert - API response:', response);
    
    if (!response.success || !response.data) {
      console.error('alertApi.updateSimplifiedAlert - API call failed:', response.error);
      throw new Error(response.error || 'Failed to update alert');
    }
    
    console.log('alertApi.updateSimplifiedAlert - Success, returning:', response.data);
    return response.data;
  },

  // Delete alert
  deleteAlert: async (alertId: number): Promise<void> => {
    console.log('alertApi.deleteAlert called with alertId:', alertId);
    const response = await apiService.request(`/alert/${alertId}`, {
      method: 'DELETE'
    });
    if (!response.success) {
      console.error('alertApi.deleteAlert failed:', response.error);
      throw new Error(response.error || 'Failed to delete alert');
    }
  },

  // Toggle alert status
  toggleAlertStatus: async (alertId: number): Promise<{ 
    message: string; 
    alertId: number; 
    isActive: boolean; 
    previousStatus: boolean 
  }> => {
    console.log('alertApi.toggleAlertStatus called with alertId:', alertId);
    const response = await apiService.request<{ 
      message: string; 
      alertId: number; 
      isActive: boolean; 
      previousStatus: boolean 
    }>(`/alert/${alertId}/toggle`, {
      method: 'PATCH'
    });
    if (!response.success || !response.data) {
      console.error('alertApi.toggleAlertStatus failed:', response.error);
      throw new Error(response.error || 'Failed to toggle alert status');
    }
    return response.data;
  },

  // Set alert status directly
  setAlertStatus: async (alertId: number, isActive: boolean): Promise<{ 
    message: string; 
    alertId: number; 
    isActive: boolean; 
    previousStatus: boolean 
  }> => {
    console.log('alertApi.setAlertStatus called with alertId:', alertId, 'isActive:', isActive);
    const response = await apiService.request<{ 
      message: string; 
      alertId: number; 
      isActive: boolean; 
      previousStatus: boolean 
    }>(`/alert/${alertId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive })
    });
    if (!response.success || !response.data) {
      console.error('alertApi.setAlertStatus failed:', response.error);
      throw new Error(response.error || 'Failed to update alert status');
    }
    return response.data;
  },

  // Get alerts by device
  getAlertsByDevice: async (deviceId: number): Promise<AlertData[]> => {
    const response = await apiService.request<AlertData[]>(`/alert/device/${deviceId}`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch device alerts');
    }
    return response.data;
  },

  // Get alert history
  getAlertHistory: async (alertId: number, page: number = 1, pageSize: number = 10): Promise<AlertHistoryItem[]> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    const response = await apiService.request<AlertHistoryItem[]>(`/alert/${alertId}/history?${params.toString()}`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch alert history');
    }
    return response.data;
  },

  // Send test alert
  sendTestAlert: async (alertId: number): Promise<{ message: string }> => {
    const response = await apiService.request<{ message: string }>(`/alert/${alertId}/test`, {
      method: 'POST'
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to send test alert');
    }
    return response.data;
  },

  // Process all alerts (Admin only)
  processAllAlerts: async (): Promise<{ message: string }> => {
    const response = await apiService.request<{ message: string }>('/alert/process', {
      method: 'POST'
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to process alerts');
    }
    return response.data;
  },

  // Get recipients for a specific alert
  getAlertRecipients: async (alertId: number): Promise<AlertRecipient[]> => {
    console.log('alertApi.getAlertRecipients called with alertId:', alertId);
    const response = await apiService.request<AlertRecipient[]>(`/alert/${alertId}/recipients`);
    console.log('alertApi.getAlertRecipients response:', response);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to get alert recipients');
    }
    return response.data;
  },


};
